import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Switch,
  Typography,
  Divider,
  Tooltip,
} from '@mui/material';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateFormFieldsSx,
  harmonateSwitchSx,
} from '../../../theme/harmonateStyles';

const ColumnCustomizationModal = ({ open, onClose, viewName, initialColumns = [], initialVisibilityModel = {}, onSave }) => {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showError } = useToast();

  useEffect(() => {
    if (open) {
      // If initialColumns are provided, use them (current state)
      // Otherwise, fetch default config from API
      if (initialColumns && initialColumns.length > 0) {
        // Use the current grid configuration (preserve width/flex/minWidth)
        const columnsWithVisibility = initialColumns.map(col => {
          const isVisible = initialVisibilityModel[col.field] !== false;
          // Ensure a finite, positive width to display in the input
          const rawWidth = col.width ?? col.minWidth;
          const resolvedWidth = Number.isFinite(rawWidth) && rawWidth > 0 ? rawWidth : 150;
          return {
            name: col.field,
            title: col.headerName || col.field,
            width: resolvedWidth,
            visible: isVisible
          };
        });
        setColumns(columnsWithVisibility);
        setLoading(false);
      } else if (viewName) {
        // Fallback to fetching default config
        fetchColumnConfig();
      }
    }
  }, [open, viewName, initialColumns, initialVisibilityModel]);

  const fetchColumnConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/config/default?category=${encodeURIComponent(viewName)}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch column configuration');
      }

      const data = await response.json();
      if (data?.grid?.columns) {
        setColumns(data.grid.columns.map(col => ({
          ...col,
          visible: col.visible !== false,
          width: col.width ? parseInt(col.width) : 150
        })));
      }
    } catch (error) {
      console.error('Error fetching column config:', error);
      showError('Failed to load column configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = (index) => {
    const newColumns = [...columns];
    newColumns[index].visible = !newColumns[index].visible;
    setColumns(newColumns);
  };

  const handleWidthChange = (index, value) => {
    const newColumns = [...columns];
    const numValue = parseInt(value) || 0;
    newColumns[index].width = numValue > 0 ? numValue : 100;
    setColumns(newColumns);
  };

  const handleTitleChange = (index, value) => {
    const newColumns = [...columns];
    newColumns[index].title = value;
    setColumns(newColumns);
  };

  const handleSave = () => {
    onSave(columns);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ ...harmonateDialogPaperProps, sx: { ...harmonateDialogPaperProps.sx, overflow: 'hidden' } }}>
      <DialogTitle sx={harmonateDialogTitleSx}>Customize - {viewName ? viewName.split('.').pop().replace(/_/g, ' ') : 'Columns'}</DialogTitle>
      <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading column configuration...</Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', mb: 2, px: 1 }}>
              <Typography variant="subtitle2" sx={{ minWidth: 220, flex: 1 }}>Column Name</Typography>
              <Typography variant="subtitle2" sx={{ width: 320, ml: 2 }}>Header Name</Typography>
              <Typography variant="subtitle2" sx={{ width: 100, ml: 2 }}>Width</Typography>
            </Box>
            <Divider />
            <Box sx={{ maxHeight: '70vh', overflowY: 'auto', pr: 1 }}>
              {columns.map((col, index) => (
                <Box
                  key={col.name}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(180px, 1fr) 300px 90px',
                    gap: 8,
                    alignItems: 'center',
                    py: 1,
                    px: 1,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Switch
                      checked={col.visible}
                      onChange={() => handleToggleVisibility(index)}
                      sx={harmonateSwitchSx}
                    />
                    <Tooltip title={col.name} placement="top">
                      <Typography noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {col.name}
                      </Typography>
                    </Tooltip>
                  </Box>

                  <TextField
                    size="small"
                    value={col.title || col.name}
                    onChange={(e) => handleTitleChange(index, e.target.value)}
                    sx={{ width: '100%' }}
                  />

                  <TextField
                    type="number"
                    size="small"
                    value={col.width}
                    onChange={(e) => handleWidthChange(index, e.target.value)}
                    inputProps={{ min: 50, max: 500 }}
                    sx={{ width: '100%' }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={loading}
          sx={harmonateProceedButtonSx}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnCustomizationModal;
